import cuid from "cuid";
import { filter, get, isEmpty, map } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { CREATE_ATTACHMENT } from "utils/api/graphql/mutations/attachment";
import {
  KEYS,
  ATTACHMENT_TYPES,
  CLIENT_CATEGORY,
  RELATIONSHIP,
} from "utils/constants";
import Template from "../Template";
import View from "./View";

export const DOCUMENTS_TYPE = {
  cin: ATTACHMENT_TYPES.CIN,
  paymentAccount: ATTACHMENT_TYPES.PAYMENT_ACCOUNT,
  healthInsuranceRightsCertificate:
    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  bankAccountDetailsForPayment:
    ATTACHMENT_TYPES.BANK_ACCOUNT_DETAILS_FOR_PAYMENT,
  proofOfEligibility: ATTACHMENT_TYPES.PROOF_OF_ELIGIBILITY,
};

const Justification = ({
  currentStatus,
  tracer,
  project,
  onBack,
  updateUrl,
  onNext,
}) => {
  const isTerritorialAgent = useMemo(
    () =>
      get(project, "contact.fields.profile") ===
      CLIENT_CATEGORY.TERRITORIAL_AGENT,
    [],
  );
  const [initialValues, setInitialValues] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_LMF.JUSTIFICATION", { returnObjects: true });
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);

  const { data: subscription } = useQuery(graphql.queries.SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });
  const titulaireUserId = get(project, "contact.user.id");

  const { loading: attachmentLoading } = useQuery(graphql.queries.ATTACHMENTS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        AND: [
          {
            subscription: { id: subscriptionId },
            user: { id: titulaireUserId },
          },
        ],
      },
      isIn: {
        type: Object.values(DOCUMENTS_TYPE),
      },
    },

    onCompleted: ({ attachments: { data: attachmentsList } }) => {
      setInitialValues({
        isTerritorialAgent,
        healthInsuranceRightsCertificate: map(
          filter(
            attachmentsList,
            ({ type }) =>
              type === ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        ),
        paymentAccount: map(
          filter(
            attachmentsList,
            ({ type }) => type === ATTACHMENT_TYPES.PAYMENT_ACCOUNT,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        ),
        cin: map(
          filter(attachmentsList, ({ type }) => type === ATTACHMENT_TYPES.CIN),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        ),
        bankAccountDetailsForPayment: map(
          filter(
            attachmentsList,
            ({ type }) =>
              type === ATTACHMENT_TYPES.BANK_ACCOUNT_DETAILS_FOR_PAYMENT,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        ),
        proofOfEligibility: map(
          filter(
            attachmentsList,
            ({ type }) => type === ATTACHMENT_TYPES.PROOF_OF_ELIGIBILITY,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        ),
        includedChildren: get(project, "fields.includedChildren"),
      });
      setLoading(false);
    },
  });
  const [addAttachment] = useMutation(CREATE_ATTACHMENT);

  const onSubmit = async ({
    includedChildren,
    isTerritorialAgent,
    ...documents
  }) => {
    setLoading(true);
    const documentsToUpload = [];
    Object.entries(documents)?.map(([key, attachments]) =>
      attachments
        ?.filter((e) => !e.status)
        .forEach(({ name, contentType, base64 }) => {
          documentsToUpload.push({
            id: cuid(),
            subscription: { id: subscriptionId },
            user: { id: titulaireUserId },
            name,
            content: base64,
            contentType,
            type: DOCUMENTS_TYPE[key],
          });
        }),
    );
    await Promise.all(
      documentsToUpload.map((document, index) =>
        addAttachment({
          variables: {
            data: document,
          },
        }),
      ),
    );
    const relatedPersons = get(project, "contact.relatedPersons", []).filter(
      (_) => _,
    );
    const hasChildren = !!relatedPersons.filter(
      ({ relationship }) => relationship === RELATIONSHIP.CHILD,
    ).length;
    const hasSpouse = !isEmpty(
      relatedPersons.find(
        ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
      ),
    );
    const source = localStorage.getItem(KEYS.SOURCE);

    updateUrl({
      subscriptionId,
      url: get(
        subscription,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });
    onNext({
      payload: {
        fields: {
          ...get(project, "fields"),
          includedChildren,
          hasRelatedPersons: {
            hasChildren,
            hasSpouse,
          },
        },
      },
    });
  };

  return (
    <Template
      progress={currentStatus.progress}
      onBack={onBack}
      loading={loading || attachmentLoading || !initialValues}
      project={project}
      View={
        <View
          key={JSON.stringify(initialValues)}
          locked={get(subscription, "subscription.locked", false)}
          onSubmit={onSubmit}
          initialValues={initialValues}
          translate={TRANSLATION}
          profile={get(project, "contact.fields.profile")}
          onBack={onBack}
        />
      }
      HeaderComponent={{
        subscription: get(subscription, "subscription"),
        tracer,
        project,
        contact: get(project, "contact"),
      }}
      contact={get(project, "contact")}
    />
  );
};
export default Justification;
